import { Card } from '@iheartjane/reefer';
import type { SpecialType } from '@iheartjane/types';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useConditionSections } from '@jane/business-admin/components/shared/specials/modal/multipleConditions/useConditionSections';
import { useRetailOrgSettings } from '@jane/business-admin/hooks/useRetailOrgSettings';
import { useStoreId } from '@jane/business-admin/hooks/useStoreId';
import type { SpecialRulesV2 } from '@jane/business-admin/types';
import { MODAL_CARD_WIDTH } from '@jane/business-admin/util';
import type {
  ConditionSet,
  LabeledRuleSets,
} from '@jane/shared-b2b/components';
import {
  addConditionSet,
  ConditionsEditor,
  convertRuleSetsToConditionSets,
  deselectConditionSetSection,
  duplicateConditionSet,
  getLabeledRuleSets,
  removeConditionSet,
  updateConditionSetApplyTo,
  updateConditionSetSelection,
} from '@jane/shared-b2b/components';

import { ProductsModal } from './productsModal';

interface Props {
  specialType?: SpecialType | '';
}

const ConditionsWrapper = ({
  conditionFor,
  editorIndex,
  ruleSets,
  specialType,
}: LabeledRuleSets & {
  editorIndex: number;
  specialType?: SpecialType | '';
}) => {
  const [conditionSets, setConditionSets] = useState<ConditionSet[]>(() =>
    convertRuleSetsToConditionSets(ruleSets)
  );
  const [syncedRuleSets, setSyncedRuleSets] = useState(ruleSets);

  const { orgId } = useRetailOrgSettings();
  const storeId = useStoreId();
  // No store id means this special is org-wide rather than per-store
  const isGlobal = storeId === '';

  /** Memoized — `useConditionSections` rebuilds every section when this changes */
  const renderProductsModal = useCallback(
    () => <ProductsModal isGlobal={isGlobal} orgId={orgId} storeId={storeId} />,
    [isGlobal, orgId, storeId]
  );

  const applyToOptions = useConditionSections({
    conditionSets,
    isGlobal,
    orgId,
    renderProductsModal,
    specialType,
    storeId,
  });

  // re-seed once the special's rules land, which happens after this mounts
  if (ruleSets !== syncedRuleSets) {
    setSyncedRuleSets(ruleSets);
    setConditionSets(convertRuleSetsToConditionSets(ruleSets));
  }

  return (
    <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
      <ConditionsEditor
        applyToOptions={applyToOptions}
        editorIndex={editorIndex}
        conditionFor={conditionFor}
        conditionSets={conditionSets}
        onAddGroup={() =>
          setConditionSets((current) => addConditionSet(current, 'attributes'))
        }
        onApplyToChange={(index, value) =>
          setConditionSets((current) =>
            updateConditionSetApplyTo(
              current,
              index,
              value,
              applyToOptions.find((option) => option.value === value)
                ?.conditions ?? []
            )
          )
        }
        onChangeGroup={(index, selectionType, key, value) =>
          setConditionSets((current) =>
            updateConditionSetSelection(
              current,
              index,
              selectionType,
              key,
              value
            )
          )
        }
        onDeleteGroup={(index) =>
          setConditionSets((current) => removeConditionSet(current, index))
        }
        onDeselectGroup={(index, key) =>
          setConditionSets((current) =>
            deselectConditionSetSection(current, index, key)
          )
        }
        onDuplicateGroup={(index) =>
          setConditionSets((current) => duplicateConditionSet(current, index))
        }
        variant="multiple"
      />
    </Card>
  );
};

export const MultipleConditions = ({ specialType }: Props) => {
  const { watch } = useFormContext<{ rules: SpecialRulesV2 }>();
  const rules = watch('rules');

  return getLabeledRuleSets(rules, specialType).map(
    ({ conditionFor, ruleSets }, index) => (
      <ConditionsWrapper
        key={index}
        editorIndex={index}
        conditionFor={conditionFor}
        ruleSets={ruleSets}
        specialType={specialType}
      />
    )
  );
};
